import React from 'react';
import { useTranslation } from 'react-i18next';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import CancelIcon from '@mui/icons-material/Cancel';
import PropTypes from 'prop-types';
import { CollapseCard, Button, DateTime, IconButton, Autocomplete } from '@totalsoft/rocket-ui';
import { Grid, Table, TableBody, TableHead, TableRow, TableCell } from '@mui/material';
import CardNoResultsWrapper from 'components/layout/CardNoResultsWrapper';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';

const PersonWorkplacesComponent = ({
  personWorkplaces,
  professionData,
  positions,
  employers,
  saveDisabled,
  onAdd,
  onModify,
  onSave,
  onDelete,
  validationPersonWorkplaces,
  clientPersonworkplacesWrite
}) => {
  const { t } = useTranslation();

  return (
    <CollapseCard id='personWorkplacesCard' icon={FilePresentIcon} title={t('OfferEdit.Client.PersonWorkplaces.Title')} toggleOnHeaderClick>
      <Grid container>
        <Grid item xs={12} sm={12} md={12} sx={{ overflowX: 'auto' }}>
          <CardNoResultsWrapper content={personWorkplaces}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('OfferEdit.Client.PersonWorkplaces.Profession')}</TableCell>
                  <TableCell>{t('OfferEdit.Client.PersonWorkplaces.Function')}</TableCell>
                  <TableCell>{t('OfferEdit.Client.PersonWorkplaces.Employer')}</TableCell>
                  <TableCell>{t('OfferEdit.Client.PersonWorkplaces.DateFrom')}</TableCell>
                  <TableCell>{t('OfferEdit.Client.PersonWorkplaces.DateTo')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {personWorkplaces?.map((item, idx) => {
                  return (
                    <TableRow key={idx}>
                      <TableCell>
                        <Autocomplete
                          value={{
                            professionId: item.professionId ?? null,
                            professionName: professionData.find(x => x.professionId === item.professionId)?.professionName ?? ''
                          }}
                          onChange={event => {
                            onModify(event.professionId, 'professionId', idx);
                          }}
                          isClearable={false}
                          valueKey='professionId'
                          labelKey='professionName'
                          options={professionData}
                          error={!isValid(validationPersonWorkplaces[idx].professionId)}
                          helperText={getErrors(validationPersonWorkplaces[idx].professionId)}
                        />
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          value={{
                            positionId: item.positionId ?? null,
                            positionName: positions.find(x => x.positionId === item.positionId)?.positionName ?? ''
                          }}
                          onChange={event => {
                            onModify(event.positionId, 'positionId', idx);
                          }}
                          isClearable={false}
                          valueKey='positionId'
                          labelKey='positionName'
                          options={positions}
                          error={!isValid(validationPersonWorkplaces[idx].positionId)}
                          helperText={getErrors(validationPersonWorkplaces[idx].positionId)}
                        />
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          value={{
                            employerId: item.employerId ?? null,
                            employerName: employers.find(x => x.employerId === item.employerId)?.employerName ?? ''
                          }}
                          onChange={event => {
                            onModify(event.employerId, 'employerId', idx);
                          }}
                          isClearable={false}
                          valueKey='employerId'
                          labelKey='employerName'
                          options={employers}
                          error={!isValid(validationPersonWorkplaces[idx].employerId)}
                          helperText={getErrors(validationPersonWorkplaces[idx].employerId)}
                        />
                      </TableCell>
                      <TableCell>
                        <DateTime
                          value={item.dateFrom}
                          onChange={event => {
                            onModify(event, 'dateFrom', idx);
                          }}
                          error={!isValid(validationPersonWorkplaces[idx].dateFrom)}
                          helperText={getErrors(validationPersonWorkplaces[idx].dateFrom)}
                        />
                      </TableCell>
                      <TableCell>
                        <DateTime
                          value={item.dateTo}
                          onChange={event => {
                            onModify(event, 'dateTo', idx);
                          }}
                          error={!isValid(validationPersonWorkplaces[idx].dateTo)}
                          helperText={getErrors(validationPersonWorkplaces[idx].dateTo)}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'end' }}>
                        <IconButton
                          id={'personWorkplaces_' + idx + '_deleteBtn'}
                          color='primary'
                          variant='text'
                          disabled={!clientPersonworkplacesWrite}
                          onClick={() => {
                            onDelete(idx);
                          }}
                        >
                          <CancelIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardNoResultsWrapper>
          <Button
            id='personWorkplaces_addBtn'
            color='primary'
            variant='contained'
            onClick={onAdd}
            sx={{ marginTop: '1.5rem' }}
            disabled={!clientPersonworkplacesWrite}
          >
            {t('Add')}
          </Button>
          <Button
            id='personWorkplaces_saveBtn'
            color='primary'
            variant='contained'
            onClick={onSave}
            disabled={saveDisabled || !isValid(validationPersonWorkplaces) || !clientPersonworkplacesWrite}
            sx={{ marginLeft: '.25rem', marginTop: '1.5rem' }}
          >
            {t('Save')}
          </Button>
        </Grid>
      </Grid>
    </CollapseCard>
  );
};
PersonWorkplacesComponent.propTypes = {
  personWorkplaces: PropTypes.array.isRequired,
  professionData: PropTypes.array.isRequired,
  positions: PropTypes.array.isRequired,
  employers: PropTypes.array.isRequired,
  saveDisabled: PropTypes.bool.isRequired,
  onAdd: PropTypes.func.isRequired,
  onModify: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  validationPersonWorkplaces: PropTypes.object,
  clientPersonworkplacesWrite: PropTypes.bool.isRequired
};
export default PersonWorkplacesComponent;
